import { lazy } from "react";

import { trackEvent } from "@/utils/mixpanel";

const STORAGE_KEY = "truth:chunk_failed_reload";
const RELOAD_COUNT_KEY = "truth:chunk_reload_count";
const MAX_RELOADS = 3;

function isChunkError(error: Error): boolean {
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("error loading dynamically imported module") ||
    error.message.includes("Importing a module script failed") ||
    error.message.includes("failed to load module script") ||
    error.message.includes("valid JavaScript MIME type") ||
    error.name === "ChunkLoadError"
  );
}

function getReloadCount(): number {
  return Number(sessionStorage.getItem(RELOAD_COUNT_KEY) || 0);
}

function handleChunkError(error: Error): void {
  if (!isChunkError(error)) return;

  const lastReload = sessionStorage.getItem(STORAGE_KEY);
  const now = Date.now();
  const reloadCount = getReloadCount();

  // Stop reloading if we've hit the max to prevent infinite reload loops
  if (reloadCount >= MAX_RELOADS) {
    trackEvent("ChunkLoadInfiniteReload", {
      "Error Message": error.message,
      "Reload Count": reloadCount,
    });
    return;
  }

  // Only auto-reload once per 30 seconds
  if (!lastReload || now - Number(lastReload) > 30_000) {
    const newCount = reloadCount + 1;
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(newCount));
    sessionStorage.setItem(STORAGE_KEY, String(now));
    trackEvent("ChunkLoadReload", {
      "Error Message": error.message,
      "Reload Count": newCount,
    });
    window.location.reload();
  }
}

/**
 * Wraps React.lazy with automatic page reload when a chunk fails to load.
 * This handles the case where a new deployment invalidates old chunk hashes
 * while users still have the old version loaded in their browser.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch((error: Error) => {
      handleChunkError(error);
      throw error;
    }),
  );
}

/**
 * Wraps a dynamic import() with the same chunk error handling.
 * Use this for non-React.lazy dynamic imports (e.g. modal loaders).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function importWithRetry<T = any>(importFn: () => Promise<T>): Promise<T> {
  return importFn().catch((error: Error) => {
    handleChunkError(error);
    throw error;
  });
}

export { importWithRetry, lazyWithRetry };
